import { useEffect, useState } from "react";

import { Callout, CalloutIntent, DialogComponent } from "widgets";

import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import { genericFilter } from "@/app-utils/filter.ts";
import { type ISelectionModel, cloneSelectionModel } from "@/app-utils/selection-model.ts";
import { type Dataset } from "@/graphql.ts";
import { removeTrailingSlash } from "@/modules/files/utils/file.js";
import { type ITableColumn } from "@/redux/modules/table-data.ts";
import { CELL_TYPE, SORT_ORDER_ASC } from "@/settings/settings.ts";

interface IDatasetPickerModalDialogProps {
  datasets: Dataset[];
  selectedDatasetIds: number[];
  isLoading?: boolean;
  hint?: string;
  exclusiveSelection?: boolean;
  onSelect: (datasetIds: number[]) => void;
  onCancel: () => void;
  globalContext?: boolean;
}

const datasetPickerGridColumnDefs: Array<Partial<ITableColumn>> = [
  {
    name: "File",
    field: "name",
    cellType: CELL_TYPE.STRING,
    sort: { direction: SORT_ORDER_ASC.toLowerCase(), priority: null },
  },
  {
    name: "Project",
    field: "project.name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Folder",
    field: "path",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Description",
    field: "description",
    cellType: CELL_TYPE.STRING,
  },
  { name: "Organisms", field: "organisms.name", groupField: "organisms", cellType: CELL_TYPE.STRING_LIST },
];

export default function DatasetPickerModalDialog(props: IDatasetPickerModalDialogProps) {
  const [datasetRows, setDatasetRows] = useState([]);
  const [filter, setFilter] = useState<string>(null);

  const initialSelectionModel = {
    selectedIds: props.selectedDatasetIds || [],
    lastIndexOperation: null,
    lastOperationIsSelection: false,
    lastSingleSelectedId: null,
  };
  const [datasetSelectionModel, setDatasetSelectionModel] = useState<ISelectionModel>(initialSelectionModel);

  useEffect(() => {
    setDatasetRows(buildDatasetRows());
  }, [props.datasets, filter]);

  return (
    <DialogComponent
      mainButton={{
        label: "Select",
        isDisabled: !datasetSelectionModel?.selectedIds.length,
        onPress: handleApplyButton,
      }}
      actions={[<AsyncSearchWithExprParser key="dataset_search" onSearchExprChange={searchExprChanged} />]}
      title={"Select a Dataset"}
      fullscreen={true}
      closeFn={props.onCancel}
      contentStyle={{ padding: 0 }}
    >
      <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
        {!props.hint && (
          <Callout intent={CalloutIntent.Info} style={{ fontWeight: "bold", margin: "10px 0px" }}>
            {props.hint}
          </Callout>
        )}
        <InMemoryTableWithSelectionModel
          className={"grid-modal-border"}
          isLoading={props.isLoading}
          uniqueGridIdentifier="dataset-picker"
          initialSelectionModel={datasetSelectionModel}
          columns={datasetPickerGridColumnDefs}
          rowGridData={datasetRows}
          useSelectionModel={true}
          exclusiveSelection={props.exclusiveSelection} // currently just one dataset can be selected
          selectionModelUpdated={(selectionModel: ISelectionModel) => {
            setDatasetSelectionModel(cloneSelectionModel(selectionModel)); // clone because the object ref does not change
          }}
          noRowsMessage={filter ? "Filter returns no match" : "No dataset available"}
        />
      </div>
    </DialogComponent>
  );

  function handleApplyButton(): void {
    // use just the first one, multiple selections will be supported later
    props.onSelect(datasetSelectionModel.selectedIds as number[]);
  }

  function buildDatasetRows() {
    return (props.datasets || [])
      .map((dataset: Dataset) => {
        return {
          id: dataset.id,
          name: dataset.name,
          organisms: dataset.fileMetadata.organisms,
          description: dataset.description,
          path: removeTrailingSlash(dataset.fileMetadata.path),
          // the following is not shown yet and the logic may require adaption when used in the genome viewer
          isDefault: props.globalContext ? dataset.isGlobalDefault : dataset.isGenomeDefault,
          project: dataset.project,
        };
      })
      .filter((dataset) => {
        return genericFilter(datasetPickerGridColumnDefs, dataset, filter);
      });
  }

  function searchExprChanged(expr: string, _terms: string[]) {
    setFilter(expr);
    return Promise.resolve();
  }
}
